import React from 'react';
import { ArrowLeft, Sparkles, Calendar, Headphones, Wrench, Heart, Users } from 'lucide-react';

const BienvenidaItem = ({ n, text }) => (
  <div className="flex gap-3 items-start">
    <div className="w-6 h-6 rounded-full bg-[#c9a227]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
      <span className="text-[#c9a227]/60 text-xs font-medium">{n}</span>
    </div>
    <p className="text-[#4a3818]/55 text-sm leading-relaxed">{text}</p>
  </div>
);

const VIEWS = {
  bienvenida: {
    icon: Sparkles,
    title: 'Bienvenida',
    subtitle: 'Tu guía para habitar este espacio',
    body: (
      <>
        <div className="bg-white border border-[#c9a227]/10 rounded-xl p-8 space-y-6">
          <p className="text-[#4a3818]/70 text-sm leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '16px' }}>
            Este es tu Campus de Interioridad. Un espacio diseñado para que puedas habitar tu proceso
            a tu ritmo, con la calma que tu sistema nervioso necesita.
          </p>
          <div className="space-y-4">
            <BienvenidaItem n="1" text="Cada mes encontrarás lecturas, meditaciones y herramientas nuevas en la sección 'Contenido del mes'." />
            <BienvenidaItem n="2" text="El Altar del Presente te muestra la energía del día: la luna, la planta, el decreto y la práctica somática." />
            <BienvenidaItem n="3" text="Tu Bitácora guarda todo lo que escribes al cruzar el umbral, tus decretos y tus resonancias." />
            <BienvenidaItem n="4" text="El Calendario Energético te revela las fechas sagradas, portales y ciclos lunares del mes." />
            <BienvenidaItem n="5" text="El Archivo preserva los contenidos de meses anteriores para que siempre puedas volver." />
          </div>
          <div className="pt-4 border-t border-[#c9a227]/10">
            <p className="text-[#8b7340]/40 text-xs italic text-center">
              No hay prisa. No hay un orden obligatorio. Confía en lo que te llame en cada momento.
            </p>
          </div>
        </div>
      </>
    ),
  },
  calendario: {
    icon: Calendar,
    title: 'Calendario Energético',
    subtitle: 'Portales, sabbats y ciclos lunares',
    body: (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
          style={{ background: 'radial-gradient(circle, rgba(201,162,39,0.08) 0%, transparent 70%)' }}>
          <Calendar className="w-6 h-6 text-[#c9a227]/30" />
        </div>
        <p className="text-[#5c4a2a]/35 text-sm font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '16px', fontStyle: 'italic' }}>
          El calendario mágico está siendo tejido...
        </p>
        <p className="text-[#5c4a2a]/35 text-xs mt-3 max-w-sm mx-auto leading-relaxed">
          Próximamente verás aquí un calendario interactivo con la energía de cada día,
          los portales numéricos, las fechas druídicas y los eclipses.
        </p>
      </div>
    ),
  },
  meditaciones: {
    icon: Headphones,
    title: 'Meditaciones y Activaciones',
    subtitle: 'Prácticas guiadas para tu proceso',
    body: (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
          style={{ background: 'radial-gradient(circle, rgba(138,122,170,0.08) 0%, transparent 70%)' }}>
          <Headphones className="w-6 h-6 text-[#8a7aaa]/30" />
        </div>
        <p className="text-[#5c4a2a]/35 text-sm font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '16px', fontStyle: 'italic' }}>
          Las activaciones del mes se están preparando...
        </p>
        <p className="text-[#5c4a2a]/35 text-xs mt-3 max-w-sm mx-auto leading-relaxed">
          Aquí encontrarás meditaciones y activaciones guiadas que se renuevan cada mes.
        </p>
      </div>
    ),
  },
  herramientas: {
    icon: Wrench,
    title: 'Herramientas',
    subtitle: 'Dinámicas mensuales para el proceso',
    body: (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
          style={{ background: 'radial-gradient(circle, rgba(184,138,90,0.08) 0%, transparent 70%)' }}>
          <Wrench className="w-6 h-6 text-[#b88a5a]/30" />
        </div>
        <p className="text-[#5c4a2a]/35 text-sm font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '16px', fontStyle: 'italic' }}>
          Las herramientas del mes están en camino...
        </p>
        <p className="text-[#5c4a2a]/35 text-xs mt-3 max-w-sm mx-auto leading-relaxed">
          Dinámicas prácticas que acompañan el contenido mensual y facilitan la integración.
        </p>
      </div>
    ),
  },
  recomendar: {
    icon: Heart,
    title: 'Recomendar',
    subtitle: 'Expande la red geométrica',
    body: (
      <div className="space-y-6">
        <div className="bg-white border border-[#c9a227]/10 rounded-xl p-8 text-center">
          <p className="text-[#4a3818]/60 text-sm leading-relaxed mb-6" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '15px' }}>
            Cada persona que se suma al Campus fortalece el campo que sostenemos entre todas.
            Es geometría sagrada: cuantas más seamos, más fuerte el tejido.
          </p>
          <p className="text-[#8b7340]/40 text-xs italic mb-6">
            Si conoces a alguien que sería feliz en este espacio, invítala a formar parte.
          </p>
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ background: 'radial-gradient(circle, rgba(201,162,39,0.08) 0%, transparent 70%)' }}>
              <Heart className="w-6 h-6 text-[#c9a227]/30" />
            </div>
            <p className="text-[#5c4a2a]/35 text-sm font-light" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '16px', fontStyle: 'italic' }}>
              El sistema de invitaciones está germinando...
            </p>
            <p className="text-[#5c4a2a]/35 text-xs mt-3 max-w-sm mx-auto">
              Próximamente podrás enviar invitaciones personales por email.
            </p>
          </div>
        </div>
      </div>
    ),
  },
  comunidad: {
    icon: Users,
    title: 'Comunidad',
    subtitle: 'Nuestro espacio compartido',
    body: (
      <div className="text-center py-16">
        <div className="w-20 h-20 mx-auto mb-5 rounded-full flex items-center justify-center"
          style={{ background: 'radial-gradient(circle, rgba(201,162,39,0.06) 0%, transparent 70%)' }}>
          <Users className="w-7 h-7 text-[#c9a227]/20" />
        </div>
        <p className="text-[#5c4a2a]/35 text-sm" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: '18px', fontStyle: 'italic' }}>
          Este espacio está germinando.
        </p>
        <p className="text-[#5c4a2a]/25 text-sm mt-2" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic' }}>
          Pronto florecerá.
        </p>
        <p className="text-[#5c4a2a]/30 text-xs mt-6 max-w-xs mx-auto leading-relaxed">
          Un lugar donde encontrarnos, compartir el camino y crear red
          desde la presencia y el respeto.
        </p>
      </div>
    ),
  },
};

const CampusDrawerView = ({ viewId, onBack }) => {
  const config = VIEWS[viewId];
  if (!config) return null;
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-[#faf8f4]" data-testid={`campus-${viewId}`}>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={onBack}
          className="flex items-center gap-2 text-[#5c4a2a]/30 hover:text-[#5c4a2a]/60 transition-colors text-sm mb-8"
          data-testid={`${viewId}-back-btn`}>
          <ArrowLeft className="w-4 h-4" /> Volver al jardín
        </button>

        <div className="text-center mb-8">
          <Icon className="w-8 h-8 text-[#5c4a2a]/25 mx-auto mb-3" />
          <h1 className="text-[#5c4a2a]/70 text-2xl font-light">{config.title}</h1>
          <p className="text-[#5c4a2a]/30 text-sm mt-2">{config.subtitle}</p>
        </div>

        {config.body}
      </div>
    </div>
  );
};

export default CampusDrawerView;
